import React, { useState, useEffect } from 'react';
import {
  getFirestore, collection, getDocs, doc, setDoc, deleteDoc
} from 'firebase/firestore';
import { v4 as uuidv4 } from 'uuid';
import {
  Box, Button, Typography, TextField, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions,
  CircularProgress, Chip, Divider, Paper
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { addHalachicTimesWidgetToCatalog } from '../../utils/addHalachicTimesWidget';

const AppCatalogManager = () => {
  const [catalog, setCatalog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  const [widgetDialog, setWidgetDialog] = useState(false);
  const [currentWidget, setCurrentWidget] = useState(null);

  const [templateDialog, setTemplateDialog] = useState(false);
  const [currentTemplate, setCurrentTemplate] = useState(null);

  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, appId: null, name: '' });

  useEffect(() => {
    loadCatalog();
  }, []);

  const loadCatalog = async () => {
    setLoading(true);
    try {
      const db = getFirestore();
      const snapshot = await getDocs(collection(db, 'APP-CATALOG'));
      const items = snapshot.docs.map(d => ({ ...d.data(), appId: d.id }));
      items.sort((a, b) => (a.name || '').localeCompare(b.name || '', 'he'));
      setCatalog(items);
    } catch (err) {
      setMessage('❌ שגיאה בטעינה: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveWidget = async () => {
    if (!currentWidget.componentKey || !currentWidget.name) {
      setMessage('❌ יש למלא שם ו-Component Key');
      return;
    }
    setSaving(true);
    try {
      const db = getFirestore();
      const widgetToSave = {
        appId: currentWidget.appId,
        id: currentWidget.appId,
        componentKey: currentWidget.componentKey.trim(),
        name: currentWidget.name.trim(),
        dashboardLayout: {
          w: Number(currentWidget.dashboardLayout?.w) || 4,
          h: Number(currentWidget.dashboardLayout?.h) || 7
        },
        templates: (currentWidget.templates || []).map(t => ({
          id: t.id,
          name: t.name.trim()
        })),
        defaultProps: currentWidget.defaultProps || {}
      };
      await setDoc(doc(db, 'APP-CATALOG', widgetToSave.appId), widgetToSave);
      setMessage('✅ נשמר בהצלחה!');
      setWidgetDialog(false);
      loadCatalog();
    } catch (err) {
      setMessage('❌ שגיאה: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteWidget = async () => {
    const { appId } = deleteConfirm;
    setDeleteConfirm({ open: false, appId: null, name: '' });
    try {
      await deleteDoc(doc(getFirestore(), 'APP-CATALOG', appId));
      setMessage('✅ נמחק בהצלחה');
      loadCatalog();
    } catch (err) {
      setMessage('❌ שגיאה: ' + err.message);
    }
  };

  const openNewWidget = () => {
    const id = uuidv4();
    setCurrentWidget({
      appId: id,
      componentKey: '',
      name: '',
      dashboardLayout: { w: 4, h: 7 },
      templates: [{ id: uuidv4(), name: 'Modern' }],
      defaultProps: {}
    });
    setWidgetDialog(true);
  };


  const openEditWidget = (widget) => {
    setCurrentWidget({ ...widget, templates: [...(widget.templates || [])] });
    setWidgetDialog(true);
  };

  const handleSaveTemplate = () => {
    if (!currentTemplate?.name?.trim()) return;
    const templates = [...(currentWidget.templates || [])];
    const idx = templates.findIndex(t => t.id === currentTemplate.id);
    if (idx >= 0) {
      templates[idx] = currentTemplate;
    } else {
      templates.push(currentTemplate);
    }
    setCurrentWidget({ ...currentWidget, templates });
    setTemplateDialog(false);
    setCurrentTemplate(null);
  };

  const handleDeleteTemplate = (templateId) => {
    setCurrentWidget({
      ...currentWidget,
      templates: currentWidget.templates.filter(t => t.id !== templateId)
    });
  };

  // ─── Quick-add predefined widget ──────────────────────────────────────
  const quickAddSeatingMap = async () => {
    const exists = catalog.find(c => c.componentKey === 'SeatingMapWidget');
    if (exists) {
      setMessage('⚠️ SeatingMapWidget כבר קיים בקטלוג');
      return;
    }
    setSaving(true);
    try {
      const db = getFirestore();
      const appId = uuidv4();
      const entry = {
        appId,
        id: appId,
        componentKey: 'SeatingMapWidget',
        name: 'מפת מושבים',
        dashboardLayout: { w: 12, h: 14 },
        templates: [
          { id: uuidv4(), name: 'Classic' },
          { id: uuidv4(), name: 'Schematic' },
        ],
        defaultProps: {},
      };
      await setDoc(doc(db, 'APP-CATALOG', appId), entry);
      setMessage('✅ SeatingMapWidget נוסף בהצלחה!');
      loadCatalog();
    } catch (err) {
      setMessage('❌ שגיאה: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const quickAddDocument = async () => {
    const exists = catalog.find(c => c.componentKey === 'DocumentWidget');
    if (exists) {
      setMessage('⚠️ DocumentWidget כבר קיים בקטלוג');
      return;
    }
    setSaving(true);
    try {
      const db = getFirestore();
      const appId = uuidv4();
      const entry = {
        appId,
        id: appId,
        componentKey: 'DocumentWidget',
        name: 'מסמך',
        dashboardLayout: { w: 6, h: 8 },
        templates: [
          { id: uuidv4(), name: 'Clean' },
        ],
        defaultProps: {},
      };
      await setDoc(doc(db, 'APP-CATALOG', appId), entry);
      setMessage('✅ DocumentWidget נוסף בהצלחה!');
      loadCatalog();
    } catch (err) {
      setMessage('❌ שגיאה: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const quickAddHalachicTimes = async () => {
    setSaving(true);
    setMessage('');
    try {
      const result = await addHalachicTimesWidgetToCatalog();
      setMessage('\u2705 ' + result.message);
      loadCatalog();
    } catch (err) {
      setMessage('\u274C \u05E9\u05D2\u05D9\u05D0\u05D4: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" fontWeight={700} color="#1e3a5f">
          ניהול APP-CATALOG
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {!catalog.find(c => c.componentKey === 'HalachicTimesWidget') && (
            <Button
              variant="outlined"
              onClick={quickAddHalachicTimes}
              disabled={saving}
              sx={{ borderColor: '#d4a843', color: '#b8860b' }}
            >
              {'\u2697\uFE0F \u05D4\u05D5\u05E1\u05E3 \u05D6\u05DE\u05E0\u05D9 \u05D4\u05D9\u05D5\u05DD \u05D4\u05D4\u05DC\u05DB\u05EA\u05D9\u05D9\u05DD'}
            </Button>
          )}
          {!catalog.find(c => c.componentKey === 'SeatingMapWidget') && (
            <Button
              variant="outlined"
              onClick={quickAddSeatingMap}
              disabled={saving}
              sx={{ borderColor: '#1e3a5f', color: '#1e3a5f' }}
            >
              🪑 הוסף מפת מושבים
            </Button>
          )}
          {!catalog.find(c => c.componentKey === 'DocumentWidget') && (
            <Button
              variant="outlined"
              onClick={quickAddDocument}
              disabled={saving}
              sx={{ borderColor: '#2563eb', color: '#2563eb' }}
            >
              📄 הוסף רכיב מסמך
            </Button>
          )}
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={openNewWidget}
            sx={{ bgcolor: '#1e3a5f' }}
          >
            הוסף ווידג'ט
          </Button>
        </Box>
      </Box>

      {message && (
        <Paper sx={{ p: 1.5, mb: 2, bgcolor: message.startsWith('✅') ? '#d4edda' : '#f8d7da' }}>
          <Typography fontSize={14}>{message}</Typography>
        </Paper>
      )}

      {loading ? (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {catalog.map(widget => (
            <Paper key={widget.appId} sx={{ p: 2, border: '1px solid #e0e0e0' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box>
                  <Typography fontWeight={700} fontSize={16} color="#1e3a5f">
                    {widget.name}
                  </Typography>
                  <Typography fontSize={12} color="#666" sx={{ mb: 1 }}>
                    {widget.componentKey} &nbsp;|&nbsp; {widget.dashboardLayout?.w}x{widget.dashboardLayout?.h}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                    {(widget.templates || []).map(t => (
                      <Chip key={t.id} label={t.name} size="small" sx={{ bgcolor: '#e8ecf5', color: '#1e3a5f' }} />
                    ))}
                  </Box>
                </Box>
                <Box>
                  <IconButton size="small" onClick={() => openEditWidget(widget)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" color="error" onClick={() => setDeleteConfirm({ open: true, appId: widget.appId, name: widget.name })}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
            </Paper>
          ))}
        </Box>
      )}

      {/* דיאלוג עריכת / יצירת ווידג'ט */}
      <Dialog open={widgetDialog} onClose={() => setWidgetDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ direction: 'rtl' }}>
          {catalog.find(c => c.appId === currentWidget?.appId) ? 'עריכת ווידג\'ט' : 'ווידג\'ט חדש'}
        </DialogTitle>
        <DialogContent sx={{ direction: 'rtl' }}>
          <TextField
            label="שם תצוגה"
            fullWidth margin="normal"
            value={currentWidget?.name || ''}
            onChange={e => setCurrentWidget({ ...currentWidget, name: e.target.value })}
          />
          <TextField
            label="Component Key"
            fullWidth margin="normal"
            placeholder="לדוגמה: MoadWidget"
            value={currentWidget?.componentKey || ''}
            onChange={e => setCurrentWidget({ ...currentWidget, componentKey: e.target.value })}
          />
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="רוחב (w)"
              type="number" margin="normal" sx={{ flex: 1 }}
              value={currentWidget?.dashboardLayout?.w || 4}
              onChange={e => setCurrentWidget({ ...currentWidget, dashboardLayout: { ...currentWidget.dashboardLayout, w: e.target.value } })}
            />
            <TextField
              label="גובה (h)"
              type="number" margin="normal" sx={{ flex: 1 }}
              value={currentWidget?.dashboardLayout?.h || 7}
              onChange={e => setCurrentWidget({ ...currentWidget, dashboardLayout: { ...currentWidget.dashboardLayout, h: e.target.value } })}
            />
          </Box>

          <Divider sx={{ my: 2 }} />

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography fontWeight={600}>תבניות</Typography>
            <Button size="small" startIcon={<AddIcon />}
              onClick={() => { setCurrentTemplate({ id: uuidv4(), name: '' }); setTemplateDialog(true); }}
            >
              הוסף תבנית
            </Button>
          </Box>

          {(currentWidget?.templates || []).map(t => (
            <Box key={t.id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1, bgcolor: '#f5f5f5', borderRadius: 1, mb: 0.5 }}>
              <Typography>{t.name}</Typography>
              <Box>
                <IconButton size="small" onClick={() => { setCurrentTemplate({ ...t }); setTemplateDialog(true); }}>
                  <EditIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" onClick={() => handleDeleteTemplate(t.id)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>
          ))}
        </DialogContent>
        <DialogActions sx={{ direction: 'rtl', px: 3, pb: 2 }}>
          <Button onClick={() => setWidgetDialog(false)}>ביטול</Button>
          <Button variant="contained" onClick={handleSaveWidget} disabled={saving} sx={{ bgcolor: '#1e3a5f' }}>
            {saving ? <CircularProgress size={20} sx={{ color: 'white' }} /> : 'שמור'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* דיאלוג אישור מחיקה */}
      <Dialog open={deleteConfirm.open} onClose={() => setDeleteConfirm({ open: false, appId: null, name: '' })}>
        <DialogTitle sx={{ direction: 'rtl' }}>אישור מחיקה</DialogTitle>
        <DialogContent sx={{ direction: 'rtl' }}>
          <Typography>האם אתה בטוח שברצונך למחוק את &quot;{deleteConfirm.name}&quot;?</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>פעולה זו אינה ניתנת לביטול.</Typography>
        </DialogContent>
        <DialogActions sx={{ direction: 'rtl', px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteConfirm({ open: false, appId: null, name: '' })}>ביטול</Button>
          <Button variant="contained" color="error" onClick={handleDeleteWidget}>מחק</Button>
        </DialogActions>
      </Dialog>

      {/* דיאלוג תבנית */}
      <Dialog open={templateDialog} onClose={() => setTemplateDialog(false)}>
        <DialogTitle sx={{ direction: 'rtl' }}>שם תבנית</DialogTitle>
        <DialogContent sx={{ direction: 'rtl' }}>
          <TextField
            label="שם"
            fullWidth margin="normal"
            placeholder="לדוגמה: Modern, Card, Times"
            value={currentTemplate?.name || ''}
            onChange={e => setCurrentTemplate({ ...currentTemplate, name: e.target.value })}
            onKeyDown={e => e.key === 'Enter' && handleSaveTemplate()}
          />
        </DialogContent>
        <DialogActions sx={{ direction: 'rtl', px: 3, pb: 2 }}>
          <Button onClick={() => setTemplateDialog(false)}>ביטול</Button>
          <Button variant="contained" onClick={handleSaveTemplate} sx={{ bgcolor: '#1e3a5f' }}>שמור</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AppCatalogManager;
